import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react'
import { Route, Switch, useRouteMatch } from 'react-router'
import useFetch, { CachePolicies } from 'use-http'

import type {
  SourcesResponse,
  UrlEndpointsResponse,
  UrlEndpointFunctionInfo,
} from '../../interfaces'
import { BaseLayout } from '../../layouts'
import { UserContext } from '../../providers'
import { UrlEndpointsForm } from '../../components/UrlEndpoints/UrlEndpointsForm'
import { ExternalStorageForm } from '../../components/ExternalStorage'
import { UrlEndpointsPage, UrlEndpointsSideMenu, UrlEndpointsHome } from '.'

const UrlEndpointsRouter = (): React.ReactElement | null => {
  const { path } = useRouteMatch()
  const { user } = useContext(UserContext)
  const [urlEndpointsRefresh, setUrlEndpointsRefresh] = useState<boolean>(false)
  const [sourcesRefresh, setSourcesRefresh] = useState<boolean>(false)
  const [urlEndpointFunctions, setUrlEndpointFunctions] = useState<
    UrlEndpointFunctionInfo[]
  >([])

  const {
    data: urlEndpoints,
    get: getUrlEndpointsFetch,
    loading: loadingUrlEndpoints,
  } = useFetch<UrlEndpointsResponse[]>({ cachePolicy: CachePolicies.NO_CACHE })
  const {
    data: sources,
    get: getSourcesFetch,
    loading: loadingSources,
  } = useFetch<SourcesResponse[]>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const {
    data,
    get: getUrlEndpointFunctionsFetch,
    loading: loadingUrlEndpointFunctions,
  } = useFetch<UrlEndpointFunctionInfo[]>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getUrlEndpoints = useMemo(
    () => async (): Promise<void> => {
      if (user?.clientNumber) {
        const resp = await getUrlEndpointsFetch(
          `/api/clients/${user.clientNumber}/url-patterns`,
        )
        if (resp) {
          window.clientDataStore = {
            ...window.clientDataStore,
            urlEndpointsLength: resp.length,
          }
          window.dispatchEvent(new CustomEvent('urlEndpointsLengthUpdated'))
        }
      }
    },
    [user],
  )

  const getSources = useMemo(
    () => async (): Promise<void> => {
      if (user?.clientNumber) {
        const resp = await getSourcesFetch(
          `/api/clients/${user.clientNumber}/sources`,
        )
        if (resp) {
          window.clientDataStore = {
            ...window.clientDataStore,
            sourceLength: resp.length,
          }
          window.dispatchEvent(new CustomEvent('sourceLengthUpdated'))
        }
      }
    },
    [user],
  )

  const getUrlEndpointFunctions = useMemo(
    () => async (): Promise<void> => {
      if (user?.clientNumber) {
        try {
          let resp = await getUrlEndpointFunctionsFetch(
            `/api/clients/${user.clientNumber}/url-endpoint-functions`,
          )

          resp = resp.filter(
            item =>
              item.latestPublishedVersion && item.latestPublishedVersion > 0,
          )
          setUrlEndpointFunctions(resp)
        } catch (error) {
          //noop
        }
      }
    },
    [user],
  )

  useEffect(() => {
    void getUrlEndpointFunctions()
  }, [getUrlEndpointFunctions])

  useEffect(() => {
    void getUrlEndpoints()
  }, [getUrlEndpoints, urlEndpointsRefresh])

  useEffect(() => {
    void getSources()
  }, [user, sourcesRefresh])

  const refreshUrlEndpoints = useCallback(() => {
    setUrlEndpointsRefresh(!urlEndpointsRefresh)
  }, [urlEndpointsRefresh])

  const refreshSources = useCallback(() => {
    setSourcesRefresh(!sourcesRefresh)
  }, [sourcesRefresh])

  return (
    <BaseLayout
      menu={
        <UrlEndpointsSideMenu
          urlEndpoints={urlEndpoints}
          loadingUrlEndpoints={loadingUrlEndpoints}
        />
      }
    >
      <Switch>
        <Route exact path={`${path}`}>
          <UrlEndpointsHome />
        </Route>

        <Route exact path={`${path}/new`}>
          <UrlEndpointsForm
            sources={sources}
            refreshUrlEndpoints={refreshUrlEndpoints}
            refreshSources={refreshSources}
            loadingUrlEndpoints={loadingUrlEndpoints}
            loadingSources={loadingSources}
            urlEndpointFunctions={urlEndpointFunctions}
          />
        </Route>

        <Route exact path={`${path}/add-new-origin`}>
          <ExternalStorageForm
            urlEndpoints={urlEndpoints}
            refreshUrlEndpoints={refreshUrlEndpoints}
            refreshSources={refreshSources}
          />
        </Route>

        <Route path={`${path}/:urlEndpointId`}>
          <UrlEndpointsPage
            urlEndpoints={urlEndpoints}
            sources={sources}
            refreshUrlEndpoints={refreshUrlEndpoints}
            refreshSources={refreshSources}
            loadingUrlEndpoints={loadingUrlEndpoints}
            loadingSources={loadingSources}
            urlEndpointFunctions={urlEndpointFunctions}
          />
        </Route>
      </Switch>
    </BaseLayout>
  )
}

export default UrlEndpointsRouter
